// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type MouseEventHandler, useCallback, useMemo, useState } from 'react';
import type { HistValue, PeriodPickerProps } from './period-picker';
import dayjs from 'dayjs';
import { useConst } from '@shared/ui-kit/hooks';

export const DEFAULT_OPTIONS: HistValue[] = [
  'YTD',
  '6M',
  '1Y',
  '3Y',
  '5Y',
  'MAX',
  '-',
];

export const YTD_VALUE = Math.abs(
  dayjs(new Date()).startOf('year').diff(dayjs(new Date()), 'day') + 1,
);

export const usePeriodPicker = ({
  defaultValue = '1Y',
  onValueChange,
  showDatePicker,
  availableOptions = DEFAULT_OPTIONS,
}: PeriodPickerProps) => {
  const periods: Array<{ label: HistValue; value: number }> = useConst(
    () =>
      [
        {
          label: 'YTD',
          value: YTD_VALUE,
        },
        {
          label: '1D',
          value: 1,
        },
        {
          label: '1W',
          value: 7,
        },
        {
          label: '1M',
          value: 30,
        },
        {
          label: '3M',
          value: 85,
        },
        {
          label: '6M',
          value: 182,
        },
        {
          label: '1Y',
          value: 365,
        },
        {
          label: '3Y',
          value: 1_095,
        },
        {
          label: '5Y',
          value: 1_825,
        },
        {
          label: 'MAX',
          value: 10_000,
        },
        ...(showDatePicker
          ? ([
              {
                label: '-',
                value: 0,
              },
            ] as const)
          : []),
      ] as const,
  );

  const values = useMemo(
    () =>
      /* istanbul ignore next */
      availableOptions === 'all'
        ? periods
        : periods.filter(({ label }) => availableOptions.includes(label)),
    [periods, availableOptions],
  );

  const enablePicker = useMemo(
    () => showDatePicker && availableOptions.includes('-'),
    [availableOptions, showDatePicker],
  );

  const [value, setValue] = useState(
    () =>
      periods.find(({ label }) => label === defaultValue)?.value ??
      periods[periods.length - 1].value,
  );

  const onItemClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      const target = event.currentTarget;

      const newValue = +target.value;

      if (newValue === value) return;

      setValue(newValue);

      onValueChange?.(newValue);
    },
    [onValueChange, value],
  );

  return {
    periods: values,
    value,
    onItemClick,
    enablePicker,
  } as const;
};
